import { css } from '@emotion/react';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  forwardRef, type MouseEventHandler, useCallback,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { useTheme } from '../../themes/theme.hooks';

const iconStyle = (props: Readonly<{
  baseColor?: string;
  hoverColor?: string;
  size?: number;
}>) => css({
  color: props.baseColor,
  fontSize: props.size ?? 16,

  '&:hover': {
    color: props.hoverColor,
  },
});

type HoverableIconProps = Readonly<{
  baseColor?: string;
  className?: string;
  disabledColor?: string;
  hoverColor: string;
  icon: IconProp;
  isDisabled?: boolean;
  size?: number;
}> & ({
  readonly title: string;
  readonly onClick?: MouseEventHandler;
} | {
  readonly onClick?: undefined;
  readonly title?: string;
});

export const HoverableIconComponent: React.FC<HoverableIconProps> = forwardRef<SVGSVGElement, HoverableIconProps>(({
  baseColor,
  className,
  disabledColor,
  hoverColor,
  icon,
  isDisabled,
  size,
  ...restProps
}, ref) => {
  const theme = useTheme();

  const getActiveOrDisabledColor = useCallback((color?: string) => {
    if (isDisabled) {
      return disabledColor || theme.textColors.secondary;
    }

    return color;
  }, [disabledColor, isDisabled, theme.textColors.secondary]);

  return (
    <FontAwesomeIcon
      {...restProps}
      ref={ref}
      className={className}
      css={iconStyle({
        baseColor: getActiveOrDisabledColor(baseColor),
        size,
        hoverColor: getActiveOrDisabledColor(hoverColor),
      })}
      icon={icon}
    />
  );
});

HoverableIconComponent.displayName = 'HoverableIcon';
